import { useEffect } from 'react';
import { useUser } from './UserContext'; // Import the custom hook for accessing the context
import { fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { setCurrentLanguage } from './i18n.js';
import { getLinkedAccounts, getCurrentAccount } from './utils/accountManager';

/**
 * Extract Cognito groups from the access token
 * Groups are stored in the 'cognito:groups' claim
 */
function getCognitoGroups(session) {
  try {
    const accessToken = session.tokens?.accessToken;
    if (!accessToken) return [];

    // The accessToken object has a payload property with the claims
    const payload = accessToken.payload;
    return payload?.['cognito:groups'] || [];
  } catch (error) {
    console.error("Error extracting Cognito groups:", error);
    return [];
  }
}

function LoadUser() {
  const { setUser, setUserData, setSession, setJwtToken, setLoadingUser, setIsAdmin } = useUser();
  const { user } = useAuthenticator();

  useEffect(() => {
    console.log("Fetching user details")
    async function fetchUserData() {
      if (user) {
        try {
          const session = await fetchAuthSession();
          const jwtToken = session.tokens.idToken.toString();
          const userAttributes = await fetchUserAttributes();

          // Check if user is admin
          const groups = getCognitoGroups(session);
          const userIsAdmin = groups.includes('admin');
          console.log("Cognito groups:", groups, "isAdmin:", userIsAdmin);
          setIsAdmin(userIsAdmin);

          // Set language preference from Cognito user attributes
          // Supported attributes: 'locale' or 'custom:locale'
          const userLocale = userAttributes.locale || userAttributes['custom:locale'] || 'en';
          setCurrentLanguage(userLocale);

          // Get linked accounts and select the current one
          const linkedAccounts = getLinkedAccounts(userAttributes, user.signInDetails.loginId);
          const currentAccount = getCurrentAccount(linkedAccounts);

          console.log("Linked accounts:", linkedAccounts);
          console.log("Current account:", currentAccount);

          const userData = {
            accountName: currentAccount.account_name,
            name: userAttributes.name,
            address: currentAccount.display_name, // Use display_name for UI rendering
            email: userAttributes.email,
            locale: userLocale,
            linkedAccounts: linkedAccounts, // Store all accounts for future account switcher
            currentAccount: currentAccount,
            isAdmin: userIsAdmin
          };
          setUser(user)
          setUserData(userData);
          setSession(session);
          setJwtToken(jwtToken);
          setLoadingUser(false);

          console.log("User details fetched")
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    }

    fetchUserData();
  }, [user, setUserData, setSession, setJwtToken, setIsAdmin]);

  return null; // This component does not render anything
}

export default LoadUser;
